import React, { useEffect, useMemo, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Layout } from '../components/layout/Layout';
import { tradePriorityApi } from '../lib/api';
import { TradePriorityConfig } from '../lib/types';
import { Button } from '../components/ui/Button';
import { Input } from '../components/ui/Input';
import { useToast } from '../components/ui/use-toast';
import { ArrowDown, ArrowUp, Loader2, Plus, RefreshCcw, Trash2 } from 'lucide-react';

interface PriorityListProps {
  title: string;
  description: string;
  values: string[];
  onMove: (index: number, delta: number) => void;
  onRemove: (symbol: string) => void;
  onAdd: (symbol: string) => void;
  newSymbol: string;
  onNewSymbolChange: (value: string) => void;
  addLabel: string;
}

const PriorityList: React.FC<PriorityListProps> = ({
  title,
  description,
  values,
  onMove,
  onRemove,
  onAdd,
  newSymbol,
  onNewSymbolChange,
  addLabel,
}) => {
  return (
    <div className="bg-white rounded-lg shadow p-6 space-y-4">
      <div>
        <h2 className="text-lg font-semibold text-gray-900">{title}</h2>
        <p className="text-sm text-gray-500">{description}</p>
      </div>

      <ul className="space-y-2">
        {values.map((symbol, index) => (
          <li
            key={`${symbol}-${index}`}
            className="flex items-center justify-between border rounded-md px-3 py-2 bg-gray-50"
          >
            <div className="flex items-center gap-3 text-sm font-medium text-gray-800">
              <span className="text-gray-500 w-6 text-right">{index + 1}.</span>
              <span>{symbol}</span>
            </div>
            <div className="flex items-center gap-1">
              <Button
                type="button"
                variant="secondary"
                size="sm"
                onClick={() => onMove(index, -1)}
                disabled={index === 0}
                title="Move up"
              >
                <ArrowUp className="h-4 w-4" />
              </Button>
              <Button
                type="button"
                variant="secondary"
                size="sm"
                onClick={() => onMove(index, 1)}
                disabled={index === values.length - 1}
                title="Move down"
              >
                <ArrowDown className="h-4 w-4" />
              </Button>
              <Button
                type="button"
                variant="destructive"
                size="sm"
                onClick={() => onRemove(symbol)}
                title="Remove symbol"
              >
                <Trash2 className="h-4 w-4" />
              </Button>
            </div>
          </li>
        ))}
      </ul>

      <div className="flex flex-col sm:flex-row gap-3 pt-2">
        <Input
          label={`${addLabel} symbol`}
          placeholder="e.g. BTCUSDT"
          value={newSymbol}
          onChange={(event) => onNewSymbolChange(event.target.value)}
        />
        <Button type="button" onClick={() => onAdd(newSymbol)} className="sm:self-end">
          <Plus className="h-4 w-4 mr-2" /> Add
        </Button>
      </div>
    </div>
  );
};

export const GlobalConfig: React.FC = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  const [longPriority, setLongPriority] = useState<string[]>([]);
  const [shortPriority, setShortPriority] = useState<string[]>([]);
  const [newLongSymbol, setNewLongSymbol] = useState('');
  const [newShortSymbol, setNewShortSymbol] = useState('');

  const priorityQuery = useQuery({
    queryKey: ['tradePriority'],
    queryFn: tradePriorityApi.get,
  });

  useEffect(() => {
    if (priorityQuery.data) {
      setLongPriority(priorityQuery.data.longPriority);
      setShortPriority(priorityQuery.data.shortPriority);
    }
  }, [priorityQuery.data]);

  const mutation = useMutation({
    mutationFn: tradePriorityApi.update,
    onSuccess: (data) => {
      queryClient.setQueryData(['tradePriority'], data);
      setLongPriority(data.longPriority);
      setShortPriority(data.shortPriority);
      toast({ title: 'Priorities updated', description: 'New trade priorities have been saved.' });
    },
    onError: (error) => {
      console.error(error);
      toast({
        title: 'Failed to save priorities',
        description: 'Please try again or contact support.',
        variant: 'destructive',
      });
    },
  });

  const formatSymbol = (value: string) => value.trim().toUpperCase().replace(/[^A-Z0-9]/g, '');

  const addSymbol = (type: 'long' | 'short', value: string) => {
    const formatted = formatSymbol(value);
    if (!formatted) {
      toast({ title: 'Enter a valid symbol', variant: 'destructive' });
      return;
    }

    const target = type === 'long' ? longPriority : shortPriority;
    if (target.includes(formatted)) {
      toast({ title: 'Symbol already exists in the list', variant: 'destructive' });
      return;
    }

    const updater = type === 'long' ? setLongPriority : setShortPriority;
    updater([...target, formatted]);
    if (type === 'long') {
      setNewLongSymbol('');
    } else {
      setNewShortSymbol('');
    }
  };

  const moveSymbol = (type: 'long' | 'short', index: number, delta: number) => {
    const target = type === 'long' ? longPriority : shortPriority;
    const newIndex = index + delta;
    if (newIndex < 0 || newIndex >= target.length) {
      return;
    }

    const updated = [...target];
    const [removed] = updated.splice(index, 1);
    updated.splice(newIndex, 0, removed);

    if (type === 'long') {
      setLongPriority(updated);
    } else {
      setShortPriority(updated);
    }
  };

  const removeSymbol = (type: 'long' | 'short', symbol: string) => {
    if (type === 'long') {
      setLongPriority(longPriority.filter((item) => item !== symbol));
    } else {
      setShortPriority(shortPriority.filter((item) => item !== symbol));
    }
  };

  const handleSave = () => {
    const payload: TradePriorityConfig = {
      longPriority,
      shortPriority,
    };
    mutation.mutate(payload);
  };

  const hasChanges = useMemo(() => {
    if (!priorityQuery.data) {
      return false;
    }
    return (
      JSON.stringify(priorityQuery.data.longPriority) !== JSON.stringify(longPriority) ||
      JSON.stringify(priorityQuery.data.shortPriority) !== JSON.stringify(shortPriority)
    );
  }, [priorityQuery.data, longPriority, shortPriority]);

  return (
    <Layout>
      <div className="space-y-6">
        <div className="flex flex-wrap justify-between items-center gap-3">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Global Configuration</h1>
            <p className="text-gray-600 text-sm">
              Manage system-wide trading priorities. Changes apply to every user immediately.
            </p>
          </div>
          <Button
            type="button"
            variant="secondary"
            onClick={() => priorityQuery.refetch()}
            disabled={priorityQuery.isFetching}
          >
            {priorityQuery.isFetching && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Refresh
          </Button>
        </div>

        {priorityQuery.isLoading ? (
          <div className="flex justify-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
          </div>
        ) : priorityQuery.isError ? (
          <div className="p-4 bg-red-50 text-red-700 rounded-md">
            Failed to load global configuration. Please try again.
          </div>
        ) : (
          <>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <PriorityList
                title="Long Priority"
                description="Trades execute in this order when placing LONG positions."
                values={longPriority}
                onMove={(index, delta) => moveSymbol('long', index, delta)}
                onRemove={(symbol) => removeSymbol('long', symbol)}
                onAdd={(symbol) => addSymbol('long', symbol)}
                newSymbol={newLongSymbol}
                onNewSymbolChange={setNewLongSymbol}
                addLabel="Long list"
              />
              <PriorityList
                title="Short Priority"
                description="Trades execute in this order when placing SHORT positions."
                values={shortPriority}
                onMove={(index, delta) => moveSymbol('short', index, delta)}
                onRemove={(symbol) => removeSymbol('short', symbol)}
                onAdd={(symbol) => addSymbol('short', symbol)}
                newSymbol={newShortSymbol}
                onNewSymbolChange={setNewShortSymbol}
                addLabel="Short list"
              />
            </div>

            <div className="flex justify-end gap-3">
              <Button
                type="button"
                variant="secondary"
                onClick={() => {
                  if (priorityQuery.data) {
                    setLongPriority(priorityQuery.data.longPriority);
                    setShortPriority(priorityQuery.data.shortPriority);
                  }
                }}
                disabled={!hasChanges || mutation.isPending}
              >
                <RefreshCcw className="h-4 w-4 mr-2" />
                Reset Changes
              </Button>
              <Button
                type="button"
                onClick={handleSave}
                disabled={!hasChanges || mutation.isPending}
              >
                {mutation.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                Save Changes
              </Button>
            </div>
          </>
        )}
      </div>
    </Layout>
  );
};
